import Store from '@/store'
import { Model } from '@vuex-orm/core'
import SectionModel from './SectionModel'
import { format, addMinutes } from 'date-fns'
import { utcToLocal, localToUtc } from '@/utils/utcConverter'
import { dateAndTime, dateSlashes, dateDashes } from '@/constants/TimeFormats'
import FieldTypes from '@/constants/FieldTypes'
import ValidationRules from '@/constants/ValidationRules'
import addClassesToModelHeaders from '@/utils/addClassesToModelHeaders'

export default class ItemModel extends Model {
  static entity = 'items'

  static nameSingular = 'Item'
  static namePlural = 'Items'

  static fields() {
    return {
      id: this.uid(),
      name: this.string(''),
      description: this.string('', (desc) =>
        desc !== 'null' ? desc : ''
      ).nullable(),
      unit: this.attr('').nullable(),
      price: this.number(0),
      order: this.number(0),
      stock: this.number(0),
      stock_delta: this.number(0),
      date: this.attr(() => localToUtc(format(new Date(), dateAndTime))),
      hidden: this.boolean(),
      sold_out: this.boolean(),
      unlimited: this.boolean(),
      section_id: this.attr(null),
      section: this.belongsTo(SectionModel, 'section_id'),
    }
  }

  static field_headers() {
    let headers = [
      {
        text: 'Name',
        align: 'start',
        value: 'name',
        width: 250,
        sortable: true,
        type: FieldTypes.TEXT,
        rules: [ValidationRules.requiredString],
        sticky: true,
      },
      {
        text: 'Section',
        value: 'section.name',
        type: FieldTypes.BELONGS_TO,
        foreignModel: SectionModel,
        align: 'start',
        sortable: true,
        sortKey: 'section',
        width: 200,
      },
      {
        text: 'Stock',
        value: 'stock',
        width: 100,
        sortable: true,
        align: 'center',
        type: FieldTypes.NUMBER,
      },
      {
        text: 'Price',
        value: 'price',
        width: 100,
        sortable: true,
        align: 'end',
        type: FieldTypes.MONEY,
        rules: [
          ValidationRules.requiredString,
          ValidationRules.unsignedNumberRule,
        ],
      },
      {
        text: 'Unit',
        value: 'unit',
        width: 80,
        sortable: true,
        type: FieldTypes.TEXT,
      },
      {
        text: 'Description',
        value: 'description',
        width: 400,
        sortable: true,
        type: FieldTypes.TEXTAREA,
      },
      {
        text: 'Sold Out',
        value: 'sold_out',
        align: 'center',
        sortable: true,
        width: 100,
        type: FieldTypes.BOOLEAN,
      },
      {
        text: 'Unlimited',
        value: 'unlimited',
        align: 'center',
        sortable: true,
        width: 110,
        type: FieldTypes.BOOLEAN,
      },
      {
        text: 'Date',
        value: 'date',
        align: 'center',
        type: FieldTypes.DATETIME,
        sortable: true,
        width: 100,
      },
      {
        text: 'Hidden',
        value: 'hidden',
        align: 'center',
        sortable: true,
        width: 100,
        type: FieldTypes.BOOLEAN,
      },
      {
        text: 'Order',
        value: 'order',
        type: FieldTypes.NUMBER,
        align: 'center',
        rules: [ValidationRules.unsignedNumberRule],
      },
    ]
    return addClassesToModelHeaders(headers)
  }

  static index_table_config() {
    return {
      dense: true,
    }
  }

  static index_quick_links() {
    return [
      {
        title: 'In Stock',
        query: { filters: { out_of_stock: 0 } },
      },
      {
        title: 'Out of Stock',
        query: { filters: { out_of_stock: 1 } },
      },
      {
        title: 'Hidden',
        query: { filters: { hidden: 1 } },
      },
    ]
  }

  /**
   * Get price adjusted in USD (cents x 100)
   */
  get price_usd() {
    return this.price / 100
  }

  /**
   * Get priced formatted like a -$D,DDD.CC
   */
  get price_formatted() {
    const prefix = this.price_usd < 0 ? '-$' : '$'
    return `${prefix}${Math.abs(this.price_usd).toFixed(2)}`
  }

  /**
   * Get santized section name
   */
  get section_name() {
    return this.section ? this.section.name.replace(/[^a-zA-Z ]/g, '') : 'N/A'
  }

  get date_in_local_tz() {
    return utcToLocal(this.date)
  }

  get date_formatted() {
    return format(this.date, dateSlashes)
  }

  get is_too_late_to_order() {
    const leadTimeMinutes = Store.getters['shop/getLeadTimeMinutes']
    const timeZone = Store.getters['shop/getTimezone'] || 'America/Los_Angeles'
    var showItemsOnOrAfter = format(
      addMinutes(new Date(), leadTimeMinutes),
      dateDashes,
      { timeZone }
    )

    return this.date < showItemsOnOrAfter
  }

  static clone(itemId) {
    const shopId = Store.getters['shop/getShopId']
    return this.api().post(`/shops/${shopId}/items/${itemId}/clone`)
  }
}
